'use client';
import { useLangueArrivee } from '@/app/lib/use-langue-arrivee';
import { LB } from '@/app/lib/lois-belges';


const CONTENT = {
  fr: {
    title: 'Conditions d\'utilisation — Portail Employé',
    subtitle: 'Applicables aux travailleurs des entreprises clientes accédant au portail Aureus Social Pro',
    updated: 'Version 1.1 — 21 septembre 2026',
    intro: 'En bref : ce portail vous permet de consulter vos fiches de paie, documents sociaux, soldes de congés et données personnelles. Aureus IA SRL agit comme prestataire technique pour votre employeur. Vos droits RGPD sont garantis.',
    s1_title: '1. Identification',
    s1_p1: 'Le portail employé est édité par Aureus IA SRL (BCE BE 1028.230.781), Square Eugène Plasky 92-94 bte 11, 1030 Schaerbeek, Bruxelles. Email : info@aureussocial.be.',
    s1_p2: 'Aureus IA SRL agit en qualité de sous-traitant au sens de l\'Art. 28 RGPD pour le compte de votre employeur (le Responsable du traitement). Le portail vous est mis à disposition par votre employeur dans le cadre de votre relation de travail.',
    s2_title: '2. Accès et identifiants',
    s2_p1: 'Votre accès au portail est créé par votre employeur. Vous recevez vos identifiants de connexion par email. Vous êtes responsable de la confidentialité de votre mot de passe et des actions effectuées depuis votre compte.',
    s2_p2: 'L\'authentification à deux facteurs (2FA) est disponible et recommandée. En cas de perte d\'accès ou de soupçon de compromission, contactez immédiatement votre employeur ou info@aureussocial.be.',
    s3_title: '3. Fonctionnalités',
    s3_li: [
      'Consulter et télécharger vos fiches de paie (PDF horodaté)',
      'Accéder à vos documents sociaux (contrats, attestations)',
      'Consulter votre solde de congés légaux et extra-légaux',
      'Mettre à jour vos coordonnées personnelles (adresse, IBAN, téléphone)',
      'Soumettre des demandes de congés, certificats médicaux',
      'Exercer vos droits RGPD (accès, rectification, portabilité)',
    ],
    s4_title: '4. Vos obligations',
    s4_li: [
      'Fournir des informations exactes et à jour',
      'Ne pas tenter d\'accéder aux données d\'autres employés',
      'Ne pas contourner les mesures de sécurité',
      'Notifier toute anomalie ou suspicion d\'accès non autorisé',
      'Respecter les usages légitimes (consultation personnelle uniquement)',
    ],
    s5_title: '5. Vos données personnelles',
    s5_p1: 'Vos données sont traitées conformément à la Politique de confidentialité. Le Responsable du traitement est votre employeur. Aureus IA SRL est le sous-traitant technique.',
    s5_p2: 'Pour exercer vos droits (accès, rectification, effacement, opposition, portabilité) : adressez-vous prioritairement à votre employeur (responsable). Aureus IA SRL peut également vous assister via info@aureussocial.be.',
    s5_p3: `Conservation des données : les données salariales et fiscales sont conservées ${LB.conservation.fiscaleAns} ans après la fin du contrat (Art. 315 CIR 1992 + obligations ONSS).`,
    s5bis_title: '5 bis. Propriété intellectuelle du logiciel',
    s5bis_p1: 'Le portail et son logiciel restent la propriété exclusive d\'Aureus IA SRL, protégée par le droit d\'auteur belge (Livre XI du Code de droit économique) et la directive 2009/24/CE relative à la protection juridique des programmes d\'ordinateur, acquise automatiquement dès sa création, sans formalité de dépôt.',
    s5bis_p2: 'L\'accès qui vous est donné est un droit d\'usage personnel, limité à la consultation de vos propres données dans le cadre de votre relation de travail. Vous ne pouvez ni reproduire, ni décompiler, ni modifier le logiciel, ni en extraire le code source.',
    s5bis_p3: 'Cette propriété intellectuelle du logiciel est distincte de vos données personnelles, qui restent les vôtres (voir section 5).',
    s6_title: '6. Sécurité',
    s6_li: [
      'Chiffrement AES-256-GCM des données sensibles (NISS, IBAN)',
      'Authentification forte 2FA disponible',
      'Isolation des données par Row Level Security',
      'Journal d\'accès complet (audit trail)',
      'Sauvegardes chiffrées quotidiennes',
      'Hébergement exclusif Union européenne (Frankfurt, Allemagne)',
    ],
    s7_title: '7. Disponibilité',
    s7_p: 'Aureus Social Pro vise une disponibilité de 99,5%. Les maintenances planifiées sont communiquées 48h à l\'avance. En cas de panne, contactez votre employeur ou info@aureussocial.be.',
    s8_title: '8. Cessation d\'accès',
    s8_p1: 'Votre accès au portail prend fin à la rupture du contrat de travail avec votre employeur. Vos données restent néanmoins conservées pendant les durées légales imposées (7 ans pour les données fiscales et salariales).',
    s8_p2: 'Vous pouvez demander un export complet de vos données au format structuré (Art. 20 RGPD) avant la cessation.',
    s9_title: '9. Modifications',
    s9_p: 'Les présentes conditions peuvent être modifiées pour refléter des évolutions légales ou techniques. Toute modification substantielle vous sera notifiée par email au moins 30 jours avant son entrée en vigueur.',
    s10_title: '10. Réclamations et droit applicable',
    s10_p1: 'Réclamation interne : adressez-vous à votre employeur ou à info@aureussocial.be. Réponse sous 30 jours maximum.',
    s10_p2: 'Autorité de Protection des Données (APD) : si vous estimez que le traitement de vos données viole le RGPD, vous pouvez introduire une réclamation auprès de l\'APD belge (Rue de la Presse 35, 1000 Bruxelles · +32 2 274 48 00 · contact@apd-gba.be).',
    s10_p3: 'Droit applicable : droit belge. Tribunaux de l\'arrondissement judiciaire de Bruxelles.',
    footer: 'Document généré le 21 septembre 2026 — Version 1.1',
  },
  nl: {
    title: 'Gebruiksvoorwaarden — Werknemersportaal',
    subtitle: 'Van toepassing op werknemers van klant-ondernemingen die toegang hebben tot het Aureus Social Pro portaal',
    updated: 'Versie 1.1 — 21 september 2026',
    intro: 'In het kort: dit portaal stelt u in staat uw loonfiches, sociale documenten, verlofsaldi en persoonsgegevens te raadplegen. Aureus IA SRL handelt als technische dienstverlener voor uw werkgever. Uw AVG-rechten zijn gewaarborgd.',
    s1_title: '1. Identificatie',
    s1_p1: 'Het werknemersportaal wordt uitgegeven door Aureus IA SRL (KBO BE 1028.230.781), Square Eugène Plasky 92-94 bte 11, 1030 Schaarbeek, Brussel. E-mail: info@aureussocial.be.',
    s1_p2: 'Aureus IA SRL treedt op als verwerker in de zin van Art. 28 AVG voor rekening van uw werkgever (de Verwerkingsverantwoordelijke). Het portaal wordt u ter beschikking gesteld door uw werkgever in het kader van uw arbeidsrelatie.',
    s2_title: '2. Toegang en inloggegevens',
    s2_p1: 'Uw toegang tot het portaal wordt door uw werkgever aangemaakt. U ontvangt uw inloggegevens per e-mail. U bent verantwoordelijk voor de vertrouwelijkheid van uw wachtwoord en de handelingen onder uw account.',
    s2_p2: 'Tweefactorauthenticatie (2FA) is beschikbaar en aanbevolen. Bij verlies van toegang of vermoeden van compromittering, contacteer onmiddellijk uw werkgever of info@aureussocial.be.',
    s3_title: '3. Functionaliteiten',
    s3_li: [
      'Loonfiches raadplegen en downloaden (getimestempte PDF)',
      'Toegang tot sociale documenten (contracten, attesten)',
      'Saldo wettelijke en bovenwettelijke verlofdagen raadplegen',
      'Persoonlijke contactgegevens bijwerken (adres, IBAN, telefoon)',
      'Verlofaanvragen, medische attesten indienen',
      'AVG-rechten uitoefenen (inzage, rectificatie, overdraagbaarheid)',
    ],
    s4_title: '4. Uw verplichtingen',
    s4_li: [
      'Accurate en actuele informatie verstrekken',
      'Niet trachten toegang te krijgen tot gegevens van andere werknemers',
      'Geen veiligheidsmaatregelen omzeilen',
      'Elke anomalie of vermoeden van ongeautoriseerde toegang melden',
      'Legitieme gebruiken respecteren (alleen persoonlijke raadpleging)',
    ],
    s5_title: '5. Uw persoonsgegevens',
    s5_p1: 'Uw gegevens worden verwerkt conform het Privacybeleid. De Verwerkingsverantwoordelijke is uw werkgever. Aureus IA SRL is de technische verwerker.',
    s5_p2: 'Om uw rechten uit te oefenen (toegang, rectificatie, wissing, bezwaar, overdraagbaarheid): wend u prioritair tot uw werkgever (verantwoordelijke). Aureus IA SRL kan u ook bijstaan via info@aureussocial.be.',
    s5_p3: `Bewaring van gegevens: loon- en fiscale gegevens worden ${LB.conservation.fiscaleAns} jaar bewaard na het einde van het contract (Art. 315 WIB 1992 + RSZ-verplichtingen).`,
    s5bis_title: '5 bis. Intellectuele eigendom van de software',
    s5bis_p1: 'Het portaal en de bijhorende software blijven de exclusieve eigendom van Aureus IA SRL, beschermd door het Belgische auteursrecht (Boek XI van het Wetboek van economisch recht) en Richtlijn 2009/24/EG betreffende de rechtsbescherming van computerprogramma\'s, automatisch verworven vanaf het ontstaan ervan, zonder registratieformaliteit.',
    s5bis_p2: 'De toegang die u krijgt, is een persoonlijk gebruiksrecht, beperkt tot de raadpleging van uw eigen gegevens in het kader van uw arbeidsrelatie. U mag de software niet reproduceren, decompileren of wijzigen, noch de broncode eruit halen.',
    s5bis_p3: 'Deze intellectuele eigendom van de software is te onderscheiden van uw persoonsgegevens, die van u blijven (zie afdeling 5).',
    s6_title: '6. Beveiliging',
    s6_li: [
      'AES-256-GCM versleuteling van gevoelige gegevens (RR-nr, IBAN)',
      'Sterke 2FA-authenticatie beschikbaar',
      'Data-isolatie via Row Level Security',
      'Volledig toegangslogboek (audit trail)',
      'Dagelijkse versleutelde back-ups',
      'Exclusieve hosting Europese Unie (Frankfurt, Duitsland)',
    ],
    s7_title: '7. Beschikbaarheid',
    s7_p: 'Aureus Social Pro streeft naar een beschikbaarheid van 99,5%. Geplande onderhoudsactiviteiten worden 48u op voorhand meegedeeld. Bij storing, contacteer uw werkgever of info@aureussocial.be.',
    s8_title: '8. Beëindiging van de toegang',
    s8_p1: 'Uw toegang tot het portaal eindigt bij beëindiging van het arbeidscontract met uw werkgever. Uw gegevens blijven echter bewaard gedurende de wettelijk voorgeschreven termijnen (7 jaar voor fiscale en loongegevens).',
    s8_p2: 'U kunt een volledige export van uw gegevens in gestructureerd formaat aanvragen (Art. 20 AVG) vóór de beëindiging.',
    s9_title: '9. Wijzigingen',
    s9_p: 'Deze voorwaarden kunnen worden gewijzigd om wettelijke of technische evoluties weer te geven. Elke substantiële wijziging wordt u minstens 30 dagen vóór inwerkingtreding per e-mail meegedeeld.',
    s10_title: '10. Klachten en toepasselijk recht',
    s10_p1: 'Interne klacht: wend u tot uw werkgever of info@aureussocial.be. Antwoord binnen 30 dagen maximum.',
    s10_p2: 'Gegevensbeschermingsautoriteit (GBA): indien u meent dat de verwerking van uw gegevens de AVG schendt, kunt u een klacht indienen bij de Belgische GBA (Drukpersstraat 35, 1000 Brussel · +32 2 274 48 00 · contact@apd-gba.be).',
    s10_p3: 'Toepasselijk recht: Belgisch recht. Rechtbanken van het gerechtelijk arrondissement Brussel.',
    footer: 'Document gegenereerd op 21 september 2026 — Versie 1.1',
  },
  en: {
    title: 'Terms of Use — Employee Portal',
    subtitle: 'Applicable to employees of client companies accessing the Aureus Social Pro portal',
    updated: 'Version 1.0 — 26 April 2026',
    intro: 'In short: this portal allows you to consult your payslips, social documents, leave balances and personal data. Aureus IA SRL acts as a technical service provider for your employer. Your GDPR rights are guaranteed.',
    s1_title: '1. Identification',
    s1_p1: 'The employee portal is published by Aureus IA SRL (BCE BE 1028.230.781), Square Eugène Plasky 92-94 bte 11, office 10, 1030 Schaerbeek, Brussels. Email: info@aureussocial.be.',
    s1_p2: 'Aureus IA SRL acts as a processor within the meaning of Art. 28 GDPR on behalf of your employer (the Data Controller). The portal is made available to you by your employer in the context of your employment relationship.',
    s2_title: '2. Access and credentials',
    s2_p1: 'Your access to the portal is created by your employer. You receive your login credentials by email. You are responsible for the confidentiality of your password and actions performed under your account.',
    s2_p2: 'Two-factor authentication (2FA) is available and recommended. In case of loss of access or suspected compromise, immediately contact your employer or info@aureussocial.be.',
    s3_title: '3. Features',
    s3_li: [
      'Consult and download your payslips (timestamped PDF)',
      'Access your social documents (contracts, certificates)',
      'Consult your legal and extra-legal leave balance',
      'Update your personal contact details (address, IBAN, phone)',
      'Submit leave requests, medical certificates',
      'Exercise your GDPR rights (access, rectification, portability)',
    ],
    s4_title: '4. Your obligations',
    s4_li: [
      'Provide accurate and up-to-date information',
      'Not attempt to access other employees\' data',
      'Not circumvent security measures',
      'Notify any anomaly or suspicion of unauthorized access',
      'Respect legitimate uses (personal consultation only)',
    ],
    s5_title: '5. Your personal data',
    s5_p1: 'Your data is processed in accordance with the Privacy Policy. The Data Controller is your employer. Aureus IA SRL is the technical processor.',
    s5_p2: 'To exercise your rights (access, rectification, erasure, objection, portability): contact your employer first (controller). Aureus IA SRL may also assist you via info@aureussocial.be.',
    s5_p3: `Data retention: payroll and tax data are retained for ${LB.conservation.fiscaleAns} years after the end of the contract (Art. 315 ITC 1992 + NSSO obligations).`,
    s6_title: '6. Security',
    s6_li: [
      'AES-256-GCM encryption of sensitive data (NRN, IBAN)',
      'Strong 2FA authentication available',
      'Data isolation by Row Level Security',
      'Complete access log (audit trail)',
      'Daily encrypted backups',
      'Exclusive hosting in European Union (Frankfurt, Germany)',
    ],
    s7_title: '7. Availability',
    s7_p: 'Aureus Social Pro aims for an availability of 99.5%. Planned maintenance is communicated 48 hours in advance. In case of failure, contact your employer or info@aureussocial.be.',
    s8_title: '8. Termination of access',
    s8_p1: 'Your access to the portal ends upon termination of the employment contract with your employer. Your data remains nonetheless retained for the legally imposed durations (7 years for tax and payroll data).',
    s8_p2: 'You may request a full export of your data in structured format (Art. 20 GDPR) before termination.',
    s9_title: '9. Modifications',
    s9_p: 'These terms may be modified to reflect legal or technical evolutions. Any substantial modification will be notified to you by email at least 30 days before its entry into force.',
    s10_title: '10. Complaints and applicable law',
    s10_p1: 'Internal complaint: contact your employer or info@aureussocial.be. Response within 30 days maximum.',
    s10_p2: 'Data Protection Authority (DPA): if you consider that the processing of your data violates GDPR, you may file a complaint with the Belgian DPA (Rue de la Presse 35, 1000 Brussels · +32 2 274 48 00 · contact@apd-gba.be).',
    s10_p3: 'Applicable law: Belgian law. Courts of the judicial district of Brussels.',
    footer: 'Document generated on 26 April 2026 — Version 1.0',
  },
  de: {
    title: 'Nutzungsbedingungen — Mitarbeiterportal',
    subtitle: 'Anwendbar auf Mitarbeiter von Kundenunternehmen mit Zugang zum Aureus Social Pro Portal',
    updated: 'Version 1.0 — 26. April 2026',
    intro: 'Kurz gesagt: Dieses Portal ermöglicht es Ihnen, Ihre Lohnabrechnungen, Sozialdokumente, Urlaubssalden und persönlichen Daten einzusehen. Aureus IA SRL handelt als technischer Dienstleister für Ihren Arbeitgeber. Ihre DSGVO-Rechte sind garantiert.',
    s1_title: '1. Identifikation',
    s1_p1: 'Das Mitarbeiterportal wird von Aureus IA SRL (ZUE BE 1028.230.781), Square Eugène Plasky 92-94 bte 11, Büro 10, 1030 Schaerbeek, Brüssel herausgegeben. E-Mail: info@aureussocial.be.',
    s1_p2: 'Aureus IA SRL handelt als Auftragsverarbeiter im Sinne von Art. 28 DSGVO im Auftrag Ihres Arbeitgebers (des Verantwortlichen). Das Portal wird Ihnen von Ihrem Arbeitgeber im Rahmen Ihres Arbeitsverhältnisses zur Verfügung gestellt.',
    s2_title: '2. Zugang und Anmeldedaten',
    s2_p1: 'Ihr Zugang zum Portal wird von Ihrem Arbeitgeber erstellt. Sie erhalten Ihre Anmeldedaten per E-Mail. Sie sind verantwortlich für die Vertraulichkeit Ihres Passworts und die unter Ihrem Konto durchgeführten Aktionen.',
    s2_p2: 'Zwei-Faktor-Authentifizierung (2FA) ist verfügbar und empfohlen. Bei Verlust des Zugangs oder Verdacht auf Kompromittierung kontaktieren Sie sofort Ihren Arbeitgeber oder info@aureussocial.be.',
    s3_title: '3. Funktionen',
    s3_li: [
      'Lohnabrechnungen einsehen und herunterladen (zeitgestempeltes PDF)',
      'Zugang zu Sozialdokumenten (Verträge, Bescheinigungen)',
      'Saldo der gesetzlichen und außergesetzlichen Urlaubstage einsehen',
      'Persönliche Kontaktdaten aktualisieren (Adresse, IBAN, Telefon)',
      'Urlaubsanträge, ärztliche Atteste einreichen',
      'DSGVO-Rechte ausüben (Auskunft, Berichtigung, Übertragbarkeit)',
    ],
    s4_title: '4. Ihre Pflichten',
    s4_li: [
      'Genaue und aktuelle Informationen bereitstellen',
      'Nicht versuchen, auf Daten anderer Mitarbeiter zuzugreifen',
      'Sicherheitsmaßnahmen nicht umgehen',
      'Jede Anomalie oder Verdacht auf unautorisierten Zugriff melden',
      'Legitime Nutzungen respektieren (nur persönliche Einsicht)',
    ],
    s5_title: '5. Ihre persönlichen Daten',
    s5_p1: 'Ihre Daten werden gemäß der Datenschutzerklärung verarbeitet. Der Verantwortliche ist Ihr Arbeitgeber. Aureus IA SRL ist der technische Auftragsverarbeiter.',
    s5_p2: 'Um Ihre Rechte auszuüben (Auskunft, Berichtigung, Löschung, Widerspruch, Übertragbarkeit): wenden Sie sich vorrangig an Ihren Arbeitgeber (Verantwortlichen). Aureus IA SRL kann Sie auch über info@aureussocial.be unterstützen.',
    s5_p3: `Datenaufbewahrung: Lohn- und Steuerdaten werden ${LB.conservation.fiscaleAns} Jahre nach Vertragsende aufbewahrt (Art. 315 EStGB 1992 + LSS-Verpflichtungen).`,
    s6_title: '6. Sicherheit',
    s6_li: [
      'AES-256-GCM-Verschlüsselung sensibler Daten (RR-Nr, IBAN)',
      'Starke 2FA-Authentifizierung verfügbar',
      'Datenisolation durch Row Level Security',
      'Vollständiges Zugriffsprotokoll (Audit Trail)',
      'Tägliche verschlüsselte Backups',
      'Ausschließliches Hosting Europäische Union (Frankfurt, Deutschland)',
    ],
    s7_title: '7. Verfügbarkeit',
    s7_p: 'Aureus Social Pro strebt eine Verfügbarkeit von 99,5% an. Geplante Wartungen werden 48 Stunden im Voraus mitgeteilt. Bei Ausfall kontaktieren Sie Ihren Arbeitgeber oder info@aureussocial.be.',
    s8_title: '8. Beendigung des Zugangs',
    s8_p1: 'Ihr Zugang zum Portal endet mit der Beendigung des Arbeitsvertrags mit Ihrem Arbeitgeber. Ihre Daten bleiben jedoch für die gesetzlich vorgeschriebenen Zeiträume aufbewahrt (7 Jahre für Steuer- und Lohndaten).',
    s8_p2: 'Sie können vor der Beendigung einen vollständigen Export Ihrer Daten in strukturiertem Format anfordern (Art. 20 DSGVO).',
    s9_title: '9. Änderungen',
    s9_p: 'Diese Bedingungen können geändert werden, um rechtlichen oder technischen Entwicklungen Rechnung zu tragen. Jede wesentliche Änderung wird Ihnen mindestens 30 Tage vor Inkrafttreten per E-Mail mitgeteilt.',
    s10_title: '10. Beschwerden und anwendbares Recht',
    s10_p1: 'Interne Beschwerde: wenden Sie sich an Ihren Arbeitgeber oder info@aureussocial.be. Antwort binnen maximal 30 Tagen.',
    s10_p2: 'Datenschutzbehörde (DSB): wenn Sie der Ansicht sind, dass die Verarbeitung Ihrer Daten gegen die DSGVO verstößt, können Sie eine Beschwerde bei der belgischen DSB einreichen (Rue de la Presse 35, 1000 Brüssel · +32 2 274 48 00 · contact@apd-gba.be).',
    s10_p3: 'Anwendbares Recht: belgisches Recht. Gerichte des Gerichtsbezirks Brüssel.',
    footer: 'Dokument erstellt am 26. April 2026 — Version 1.0',
  },
};

export default function CGUEmployePage() {
  const [lang, setLang] = useLangueArrivee(Object.keys(CONTENT));

  const t = CONTENT[lang];

  const S = {
    h1: { fontSize: 26, fontWeight: 800, color: '#c6a34e', margin: '0 0 8px', letterSpacing: '-0.5px' },
    subtitle: { fontSize: 12, color: '#888', margin: '0 0 24px', lineHeight: 1.6 },
    section: { marginBottom: 28 },
    h2: { fontSize: 16, fontWeight: 700, color: '#e8e6e0', margin: '0 0 12px', paddingBottom: 8, borderBottom: '1px solid rgba(198,163,78,.12)' },
    p: { fontSize: 13, color: '#b0ada6', lineHeight: 1.8, margin: '0 0 10px' },
    updated: { fontSize: 10, color: '#666', marginBottom: 32, fontStyle: 'italic' },
    box: { background: 'rgba(198,163,78,.05)', border: '1px solid rgba(198,163,78,.15)', borderRadius: 8, padding: 16, margin: '12px 0', fontSize: 12, color: '#c6a34e' },
    li: { fontSize: 13, color: '#b0ada6', lineHeight: 1.8, marginLeft: 20 },
    langSelector: { display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' },
    langBtn: (active) => ({
      padding: '6px 14px', borderRadius: 6, border: `1px solid ${active ? '#c6a34e' : 'rgba(198,163,78,.2)'}`,
      background: active ? 'rgba(198,163,78,.15)' : 'transparent', color: active ? '#c6a34e' : '#888',
      cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
    }),
  };

  return (
    <div>
      <div style={S.langSelector}>
        {['fr', 'nl'].map(l => (
          <button key={l} onClick={() => setLang(l)} style={S.langBtn(lang === l)}>{l.toUpperCase()}</button>
        ))}
      </div>

      <h1 style={S.h1}>{t.title}</h1>
      <p style={S.subtitle}>{t.subtitle}</p>
      <p style={S.updated}>{t.updated}</p>

      <div style={S.box}><strong>{t.intro}</strong></div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s1_title}</h2>
        <p style={S.p}>{t.s1_p1}</p>
        <p style={S.p}>{t.s1_p2}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s2_title}</h2>
        <p style={S.p}>{t.s2_p1}</p>
        <p style={S.p}>{t.s2_p2}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s3_title}</h2>
        <ul>{t.s3_li.map((l, i) => <li key={i} style={S.li}>{l}</li>)}</ul>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s4_title}</h2>
        <ul>{t.s4_li.map((l, i) => <li key={i} style={S.li}>{l}</li>)}</ul>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s5_title}</h2>
        <p style={S.p}>{t.s5_p1}</p>
        <p style={S.p}>{t.s5_p2}</p>
        <p style={S.p}>{t.s5_p3}</p>
      </div>

      {t.s5bis_title && (
        <div style={S.section}>
          <h2 style={S.h2}>{t.s5bis_title}</h2>
          <p style={S.p}>{t.s5bis_p1}</p>
          <p style={S.p}>{t.s5bis_p2}</p>
          <p style={S.p}>{t.s5bis_p3}</p>
        </div>
      )}

      <div style={S.section}>
        <h2 style={S.h2}>{t.s6_title}</h2>
        <ul>{t.s6_li.map((l, i) => <li key={i} style={S.li}>{l}</li>)}</ul>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s7_title}</h2>
        <p style={S.p}>{t.s7_p}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s8_title}</h2>
        <p style={S.p}>{t.s8_p1}</p>
        <p style={S.p}>{t.s8_p2}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s9_title}</h2>
        <p style={S.p}>{t.s9_p}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s10_title}</h2>
        <p style={S.p}>{t.s10_p1}</p>
        <p style={S.p}>{t.s10_p2}</p>
        <p style={S.p}>{t.s10_p3}</p>
      </div>

      <div style={{ marginTop: 40, padding: 20, border: '1px solid rgba(198,163,78,.2)', borderRadius: 8, textAlign: 'center', fontSize: 11, color: '#888' }}>
        Aureus IA SRL — BCE BE 1028.230.781 — info@aureussocial.be<br />
        <em>{t.footer}</em>
      </div>
    </div>
  );
}
